/**
 * SfxPromptInput Component
 *
 * Textarea with category suggestion chips for sound effect prompts.
 */

import { memo, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { SFX_PROMPT_CATEGORIES, type PromptCategory } from './SfxPromptInput.constants';

// ============================================================================
// Types
// ============================================================================

interface SfxPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const SfxPromptInput = memo(function SfxPromptInput({
  value,
  onChange,
  disabled = false,
  className = '',
}: SfxPromptInputProps) {
  const { t } = useTranslation();

  const handleCategoryClick = useCallback(
    (category: PromptCategory) => {
      if (disabled) return;
      // Pick random prompt from category
      const randomPrompt = category.prompts[Math.floor(Math.random() * category.prompts.length)];
      onChange(randomPrompt);
    },
    [disabled, onChange]
  );

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label-icon">
        <Sparkles className="h-4 w-4 flex-shrink-0" />
        <span>
          {t('soundEffects.promptInput.title', { defaultValue: 'Sound Effect Description' })}
        </span>
      </label>
      <p className="text-xs text-white/50 mb-3">
        {t('soundEffects.promptInput.description', {
          defaultValue: 'Describe the sound effect you want to generate',
        })}
      </p>

      {/* Category Chips */}
      <div className="mb-3">
        <p className="text-xs text-white/40 mb-2">
          {t('soundEffects.promptInput.categoriesLabel', {
            defaultValue: 'Quick suggestions:',
          })}
        </p>
        <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
          {SFX_PROMPT_CATEGORIES.map((category) => (
            <button
              key={category.label}
              type="button"
              onClick={() => handleCategoryClick(category)}
              disabled={disabled}
              className={`
                px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap
                transition-all min-h-[32px]
                bg-black/30 border border-white/10 text-white/70
                hover:bg-purple-900/30 hover:border-purple-500/30 hover:text-purple-300
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              title={t('soundEffects.promptInput.categoryTooltip', {
                defaultValue: 'Click for random {{category}} prompt',
                category: category.label.toLowerCase(),
              })}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Textarea */}
      <textarea
        value={value}
        onChange={(e) => !disabled && onChange(e.target.value)}
        disabled={disabled}
        placeholder={t('soundEffects.promptInput.placeholder', {
          defaultValue: 'e.g., dramatic cinematic impact hit with reverb, ambient forest sounds...',
        })}
        rows={4}
        className={`
          w-full px-4 py-3 rounded-lg
          bg-black/20 border border-white/10
          text-white placeholder-white/30
          focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/30
          transition-all resize-none
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      />

      {/* Character count */}
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-white/40">
          {t('soundEffects.promptInput.hint', {
            defaultValue: 'Be specific and descriptive for best results',
          })}
        </span>
        <span className="text-white/30">{value.length} characters</span>
      </div>
    </div>
  );
});

export default SfxPromptInput;
